import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { PhoneNumberOwnerType } from '../../common/enums.js';
import { t } from '../../common/i18n/index.js';
import { Checkbox } from '../../common/react-hook-form/components/Checkbox.js';
import { SelectRadio } from '../../common/react-hook-form/components/SelectRadio.js';
import { Email } from '../../common/react-hook-form/fields/Email.js';
import type { PhoneNumberOwner } from '../../common/types/subscription.js';

import './NumberOwnerRHF.scss';

type AllowedPaddingClassName = 'ds-padding-top--2' | 'ds-padding-top--4';

export interface NumberOwnerProps {
  phoneNumberOwner?: PhoneNumberOwner;
  personOwnerEmailLabel?: string;
  companyName: string;
  cartItemInstanceId: string;
  topPaddingClassName?: AllowedPaddingClassName;
  headerLevel?: 'h4' | 'h5';
}

export const NumberOwnerRHF = ({
  phoneNumberOwner,
  personOwnerEmailLabel = '',
  cartItemInstanceId,
  companyName,
  topPaddingClassName = dsClass.PADDING_TOP_2 as AllowedPaddingClassName,
  headerLevel = 'h4',
}: NumberOwnerProps) => {
  const Header = headerLevel;

  const {
    watch,
    setValue,
    formState: { isReady },
  } = useFormContext();
  const numberOwnerType = watch(`configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.type`);
  useEffect(() => {
    if (isReady && numberOwnerType === undefined) {
      // TODO watch is not able to see Controller defaultValue, so need to update it
      setValue(`configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.type`, PhoneNumberOwnerType.PERSON);
    }
  }, [isReady, numberOwnerType, cartItemInstanceId, setValue]);

  return (
    <div className={`phone-number-owner ${topPaddingClassName || ''}`} id="number-privacy">
      <Header className={dsClass.MARGIN_BOTTOM_3}>{t.O6Q1('Current owner of number?')}</Header>
      <SelectRadio
        name={`configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.type`}
        defaultValue={phoneNumberOwner?.ownerType ?? PhoneNumberOwnerType.PERSON}
        items={[
          {
            label: t.PCB2('Person'),
            value: PhoneNumberOwnerType.PERSON,
          },
          {
            label: companyName,
            value: PhoneNumberOwnerType.COMPANY,
          },
        ]}
      />
      {numberOwnerType === PhoneNumberOwnerType.PERSON && (
        <div className="phone-number-owner-rhf-person-email">
          <Email
            name={`configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.personEmail`}
            hint={t.P5H4(
              'A verification request will be sent to the address given. The user accepts the number transfer, by using online banking credentials for authentication.'
            )}
            label={personOwnerEmailLabel}
          />
        </div>
      )}
      {numberOwnerType === PhoneNumberOwnerType.COMPANY && (
        <Checkbox
          name={`configuration.${cartItemInstanceId}.selectPhoneNumber.numberOwner.permitDonoringOnFixedContract`}
          label={t.KD2L(
            'I give permission for the transfer of my number, even though a fixed-term contract may be ongoing.'
          )}
        />
      )}
    </div>
  );
};
